import { useEffect, useState } from 'react';
import AssistantResource from 'Resources/AssistantChat';
import AgentsResource from 'Resources/Agents';
import { notifyAIWorkspaceError } from '../notifyAIWorkspaceError';

export type AssistantIdentity = {
	assistantName: string;
	avatarUrl: string | null;
	personalAgentId: number | null;
};

// Resolves the user's assistant identity the same way AIWorkspace does: personal agent
// first, falling back to the assistant profile. Used by EmbedAIChat and FloatingAIChat.
export function usePersonalAssistantIdentity(selfId?: number): AssistantIdentity {
	const [assistantName, setAssistantName] = useState('Upsales AI');
	const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
	const [personalAgentId, setPersonalAgentId] = useState<number | null>(null);

	useEffect(() => {
		if (!selfId) return;
		const loadProfile = () =>
			AssistantResource.getProfile()
				.then(profile => {
					if (profile.name) setAssistantName(profile.name);
					if (profile.avatarUrl) setAvatarUrl(profile.avatarUrl);
				})
				.catch(err => notifyAIWorkspaceError(err, 'getProfile'));
		AgentsResource.list({ visibility: 'personal' })
			.then(agents => {
				const primary = agents
					.filter(a => a.ownerUserId === selfId)
					.sort((a, b) => new Date(a.regDate).getTime() - new Date(b.regDate).getTime())[0];
				if (primary) {
					setPersonalAgentId(primary.id);
					setAssistantName(primary.name || 'Upsales AI');
					setAvatarUrl(primary.avatarUrl || null);
				} else {
					loadProfile();
				}
			})
			.catch(err => {
				notifyAIWorkspaceError(err, 'listPersonalAgents');
				loadProfile();
			});
	}, [selfId]);

	useEffect(() => {
		const onIdentity = (e: Event) => {
			const { name, avatarUrl: url } = (e as CustomEvent).detail ?? {};
			if (name) setAssistantName(name);
			if (url !== undefined) setAvatarUrl(url);
		};
		window.addEventListener('ws-identity-updated', onIdentity);
		return () => window.removeEventListener('ws-identity-updated', onIdentity);
	}, []);

	return { assistantName, avatarUrl, personalAgentId };
}
